/**
 * Route guards.
 *
 * The brief calls out that an unauthorised page must not flash before redirecting.
 * That is handled structurally:
 *   1. While auth is resolving, NO route element mounts — we render a shell skeleton.
 *   2. Redirects happen during render via <Navigate>, not in an effect, so React
 *      Router processes them in the same commit.
 */

import { Link, Navigate, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { ShieldX } from 'lucide-react'
import { useAuth } from '@/app/providers/AuthProvider'
import { usePermissions } from '@/app/providers/TenantProvider'
import { hasAnyPermission, type Permission } from '@/lib/permissions'
import { Button } from '@/components/ui/Button'

export function FullPageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas" aria-busy="true">
      <span className="sr-only">Loading</span>
      <div className="flex flex-col items-center gap-3">
        <div className="size-6 animate-spin rounded-full border-2 border-border-strong border-t-accent" />
      </div>
    </div>
  )
}

export function Forbidden({ needed }: { needed?: Permission[] }) {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center">
      <div className="mb-3 flex size-10 items-center justify-center rounded-full bg-warning-subtle text-warning">
        <ShieldX className="size-5" aria-hidden />
      </div>
      <h1 className="text-lg font-semibold text-fg">You do not have access to this page</h1>
      <p className="mt-1 max-w-md text-sm text-fg-muted">
        Your role does not include
        {needed?.length ? (
          <> the {needed.map((p) => <code key={p} className="mx-1 font-mono text-xs">{p}</code>)} permission.</>
        ) : (
          ' the permission needed here.'
        )}{' '}
        Ask an administrator in your organisation if you need it.
      </p>
      <Button variant="secondary" className="mt-4" asChild>
        <Link to="/dashboard">Back to dashboard</Link>
      </Button>
    </div>
  )
}

/** Requires a session. Preserves the attempted URL so login can return to it. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth()
  const location = useLocation()

  if (status === 'loading') return <FullPageLoader />
  if (status === 'unauthenticated') {
    const next = encodeURIComponent(`${location.pathname}${location.search}`)
    return <Navigate to={`/login?next=${next}`} replace />
  }
  return <>{children}</>
}

/** Requires at least one of the listed permissions. */
export function RequirePermission({
  children,
  any,
}: {
  children: ReactNode
  any: Permission[]
}) {
  const permissions = usePermissions()

  // Permissions arrive with bootstrap. Until they do, show the loader rather than
  // a Forbidden page we would immediately replace.
  if (permissions.length === 0) return <FullPageLoader />
  if (!hasAnyPermission(permissions, any)) return <Forbidden needed={any} />
  return <>{children}</>
}

/** Keeps an authenticated user away from /login. */
export function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { status } = useAuth()
  if (status === 'loading') return <FullPageLoader />
  if (status === 'authenticated') return <Navigate to="/dashboard" replace />
  return <>{children}</>
}
